'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import { useEffect, useRef, useState } from 'react';

import { useLogger, useTracking } from '@/analytics';
import { ButtonLink } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';
import { AffiliateId } from '@/models';

import { SCENARIOS } from '../data';
import { computeFamilyVerdict, computeScore, GameState } from '../engine';

import { FamilyAvatar } from './FamilyAvatar';
import { IconThermometer, moodIcon } from './icons';
import { deliverVerdictFile, renderVerdictFile, shareVerdictCard } from './shareVerdictCard';

type Props = {
  onRestart: () => void;
  state: GameState;
};

const StatTile = ({ icon: Icon, label }: { icon: DS.IIconComponent; label: string }) => (
  <x.div alignItems="center" bg="uiBgAlternate" borderRadius={8} display="flex" gap={2} justifyContent="center" p={3}>
    <Icon size={4} />
    <x.b>{label}</x.b>
  </x.div>
);

export const Scorecard = ({ onRestart, state }: Props) => {
  const { addError } = useLogger();
  const { trackEvent } = useTracking();
  const score = computeScore(state);
  const verdict = computeFamilyVerdict(state);
  const scenarioName = SCENARIOS[state.prep.scenarioId].name;
  const survived = !state.dark && score.batteryNeverEmpty;

  const logoRef = useRef<HTMLSpanElement>(null);
  const [shareFile, setShareFile] = useState<File | null>(null);

  // Pre-render the share card: iOS Safari requires navigator.share to run synchronously inside the
  // tap gesture, so the file must already exist when the button is clicked. State is stable once the
  // scorecard is reached, so this renders once.
  useEffect(() => {
    const logoSvg = logoRef.current?.querySelector('svg')?.outerHTML;
    renderVerdictFile({
      logoSvg,
      scenarioName: SCENARIOS[state.prep.scenarioId].name,
      verdict: computeFamilyVerdict(state),
    })
      .then(setShareFile)
      .catch((err) => addError(err));
  }, [addError, state]);

  const handleShare = () => {
    trackEvent(TRACKING.BLACKOUT_BATTLE_CLICKED_SHARE, { rating: verdict.rating, scenario: state.prep.scenarioId });
    const logoSvg = logoRef.current?.querySelector('svg')?.outerHTML;
    const delivery = shareFile ? deliverVerdictFile(shareFile) : shareVerdictCard({ logoSvg, scenarioName, verdict });
    delivery.catch((err) => {
      // user dismissed the native share sheet — not an error
      if (err instanceof DOMException && err.name === 'AbortError') return;
      addError(err);
    });
  };

  return (
    <DS.Flex alignItems="center" gap={5} maxW="480px" mx="auto" textAlign="center">
      <DS.Text as="h2" text={{ _: 'h2', md: 'd3' }}>
        {survived ? 'You kept the lights on!' : 'Power restored!'}
      </DS.Text>

      <x.section bg="uiBgAlternate" borderRadius={12} p={5} textAlign="left" w="100%">
        <DS.Text color="uiHelperCopy" text="body14" textAlign="center">
          The family rates your blackout parenting
        </DS.Text>
        <DS.Text as="p" text="h3" textAlign="center">
          {`${verdict.rating}/10`}
        </DS.Text>
        {verdict.lines.map(({ memberId, name, text }) => (
          <x.div alignItems="flex-start" display="flex" gap={2} key={memberId} mt={3}>
            <FamilyAvatar memberId={memberId} />
            <x.div>
              <DS.Text color="uiHelperCopy" fontWeight="bold" text="body12">
                {name}
              </DS.Text>
              <DS.Text as="p" text="body14">
                {text}
              </DS.Text>
            </x.div>
          </x.div>
        ))}
        {/* DS buttons cap at max-width 360px — lift it so fullWidth actually fills the card */}
        <DS.Button fullWidth maxW="100%" mt={4} onClick={handleShare} variant="secondary">
          Share your verdict
        </DS.Button>
        {/* hidden render of the current wordmark — the share card rasterizes this exact SVG */}
        <x.span display="none" ref={logoRef}>
          <DS.IconLogo />
        </x.span>
      </x.section>

      <x.div bg="uiBgAlternate" borderRadius={12} p={5} w="100%">
        <DS.Text color="uiHelperCopy" text="body14">
          Your score
        </DS.Text>
        <DS.Text as="p" text="d2">
          {String(score.total)}
        </DS.Text>
        <DS.Text as="p" fontWeight="bold" text="h6">
          {score.title}
        </DS.Text>
        {score.tierMultiplier > 1 && (
          <DS.Text as="p" color="uiHelperCopy" text="body14">
            {`Includes a ×${score.tierMultiplier} small-budget bonus`}
          </DS.Text>
        )}
        {score.overtimeHours > 0 && (
          <DS.Text as="p" color="uiHelperCopy" text="body14">
            {`+${score.overtimeHours}h off-grid overtime`}
          </DS.Text>
        )}
      </x.div>

      <x.div display="grid" gap={2} gridTemplateColumns="1fr 1fr" w="100%">
        <StatTile
          icon={score.batteryNeverEmpty ? DS.IconBatteryLightningVerticalFill : DS.IconBatteryLightningVertical}
          label={score.batteryNeverEmpty ? 'Never hit 0%' : 'Went dark'}
        />
        <StatTile icon={moodIcon(score.avgMood)} label={`Mood ${Math.round(score.avgMood)}/100`} />
        <StatTile icon={IconThermometer} label={`Comfy ${Math.round(score.comfortPct * 100)}%`} />
        <StatTile icon={DS.IconPlug} label={`Ended at ${Math.round(score.endingBatteryPct * 100)}%`} />
      </x.div>

      <x.div bg="uiBgTertiary" borderRadius={12} p={5} w="100%">
        <DS.Text as="h3" text="h6">
          Now imagine it’s your real house
        </DS.Text>
        <DS.Text as="p" mt={2} text="body14">
          Solar + battery setups handle this in real life — quietly, automatically, no group-chat triage required. See
          what one could look like on your roof.
        </DS.Text>
        <ButtonLink
          affiliateId={AffiliateId.MINITOOLS}
          event={{ name: TRACKING.BLACKOUT_BATTLE_CLICKED_CTA }}
          fullWidth
          mt={3}
          path="/quote-builder"
          variant="cta"
        >
          Get real quotes for your home
        </ButtonLink>
      </x.div>

      <DS.Button onClick={onRestart} variant="secondary">
        Play again
      </DS.Button>
    </DS.Flex>
  );
};
